/**
 * The login wall + paywall stub shown by the entry gate (sc-6940,
 * email-OTP sc-7039).
 *
 * Login wall: sells the 5 free daily tokens (not just "sign in"), offers
 * [Continue with Google] (primary, redirect) and an email path that is
 * now EMAIL-OTP (sc-7039): enter email → 6-digit code arrives → enter it
 * → signed in IN PLACE (no redirect). Magic-link is demoted to a small
 * secondary "email me a link instead" fallback.
 *
 * Google leaves the page (redirect → /auth/callback), so we stash the
 * pending cartridge for relaunch. Email-OTP verifies without leaving, so
 * it establishes the session directly and relaunches the pending
 * cartridge itself via onSignedIn().
 *
 * Paywall (grant:'paywall') is the real pricing surface (sc-6943): three
 * passes (day $0.99 / week $2.99 / month $8.99), one-tap buy → PayPal
 * approve redirect. The player returns to /billing/return, which captures
 * (day pass) and relaunches the pending cartridge. Prices/copy come from
 * the shared PASS_CATALOG; the server is the source of truth for charges.
 */

import { PASS_CATALOG, type PassOption } from '@arcraz/arcadia-types';
import { oauthStart, startCheckout } from '../api/client.js';
import { AuthApiError, sendEmailOtp, sendMagicLink, verifyEmailOtp } from '@arcraz/arcadia-auth-web';

/**
 * Staging/dev domain gate (REGISTRATION_DOMAIN_RESTRICTED). On a restricted
 * environment the API returns this code when the email's domain isn't on the
 * allowlist. We show a friendly, specific message instead of the generic
 * "that didn't work" — this intentionally reveals the allowlist, which is
 * fine for a non-secret dev fence (prod leaves the allowlist empty, so this
 * never fires there).
 */
const DEV_DOMAIN_MSG =
  'This is a restricted environment — sign-in is limited to @arcraz.com accounts.';
function authErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof AuthApiError && err.code === 'REGISTRATION_DOMAIN_RESTRICTED') {
    return DEV_DOMAIN_MSG;
  }
  return err instanceof Error ? fallback.replace('%m', err.message) : 'Something went wrong.';
}
import { establishSession } from '../auth/establish.js';
import { getAdManager } from '../ads/manager.js';
import { openCheckoutModal } from '../billing/checkout-modal.js';
import { isPayPalSdkConfigured } from '../billing/paypal-sdk.js';

/** Outcome of the paywall — lets a caller relaunch when the player can now play. */
export interface PaywallResult {
  /** True when the player earned a token via rewarded video (can now play). */
  tokenEarned: boolean;
  /** True when the player bought a pass in-page (entitled now; can play). */
  entitled?: boolean;
}

/** The player can launch a run now — either path resolved to playable access. */
export function paywallGrantsPlay(r: PaywallResult): boolean {
  return r.tokenEarned || r.entitled === true;
}

/** Cabinet paths PayPal returns the buyer to (handled on boot). */
const BILLING_RETURN_PATH = '/billing/return';
const BILLING_CANCEL_PATH = '/billing/cancel';

const PENDING_LAUNCH_KEY = 'a84.pendingLaunch';
const OAUTH_STATE_KEY = 'a84.oauth.state';
const OAUTH_PROVIDER_KEY = 'a84.oauth.provider';

/** Remember the cartridge to auto-launch after the auth round-trip. */
export function setPendingLaunch(cartridgeId: string): void {
  try {
    sessionStorage.setItem(PENDING_LAUNCH_KEY, cartridgeId);
  } catch {
    /* private mode — auto-relaunch just won't happen; login still works */
  }
}

/** Read + clear the pending cartridge (called on boot after callback). */
export function consumePendingLaunch(): string | null {
  try {
    const id = sessionStorage.getItem(PENDING_LAUNCH_KEY);
    sessionStorage.removeItem(PENDING_LAUNCH_KEY);
    return id;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------- overlay

let activeWall: HTMLElement | null = null;

function dismiss(): void {
  activeWall?.remove();
  activeWall = null;
}

/**
 * Show the login wall. Returns a promise that resolves when the player
 * dismisses it (back to gallery); the auth actions navigate away, so
 * those paths never resolve here.
 *
 * Two callers:
 *   - The entry gate (INSERT COIN, taste used) passes the cartridge the
 *     player was trying to coin, plus onSignedIn so an in-place email-OTP
 *     sign-in can relaunch it.
 *   - The footer "SIGN IN" button passes nothing — a bare sign-in.
 *
 * `onSignedIn` fires only for the IN-PLACE email-OTP path (the player is
 * signed in without leaving the page). Google/magic-link leave the page
 * and resume via /auth/callback instead, so they never call it.
 */
export function showLoginWall(
  cartridgeId?: string,
  // Reserved for a future "…to play NEON GRIND" personalization; kept in the
  // positional signature so callers (grid.ts) don't need touching.
  _cartridgeTitle?: string,
  onSignedIn?: () => void
): Promise<void> {
  return new Promise((resolve) => {
    dismiss();
    const refs = buildWall('login');
    const {
      root, googleBtn, emailBtn, emailForm, emailInput, emailSend,
      codeForm, codeInput, codeVerify, magicLinkBtn, status, closeBtn,
    } = refs;
    activeWall = root;
    document.body.appendChild(root);

    const close = (): void => { dismiss(); resolve(); };
    closeBtn.addEventListener('click', close);
    root.addEventListener('click', (e) => { if (e.target === root) close(); });

    // Google: stash the pending cartridge (if the gate gave us one) +
    // provider, ask the API for the authorization URL, then redirect.
    // `state` is stashed for the callback's CSRF check.
    googleBtn.addEventListener('click', () => {
      void (async () => {
        googleBtn.disabled = true;
        status.textContent = 'Redirecting to Google…';
        try {
          if (cartridgeId) setPendingLaunch(cartridgeId);
          const { authorizationUrl, state } = await oauthStart(
            'google',
            `${window.location.origin}/auth/callback`
          );
          sessionStorage.setItem(OAUTH_STATE_KEY, state);
          sessionStorage.setItem(OAUTH_PROVIDER_KEY, 'google');
          window.location.assign(authorizationUrl);
        } catch (err) {
          googleBtn.disabled = false;
          status.textContent =
            err instanceof Error ? `Couldn't start Google sign-in: ${err.message}` : 'Sign-in failed.';
        }
      })();
    });

    // Email: reveal the email input (step 1 of the OTP flow).
    emailBtn.addEventListener('click', () => {
      emailForm.hidden = false;
      emailInput.focus();
    });

    // Remembered between the send + verify steps.
    let otpEmail = '';
    const emailOk = (e: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

    // Step 1 — send the OTP code to the email.
    const sendCode = (): void => {
      const email = emailInput.value.trim();
      if (!emailOk(email)) {
        status.textContent = 'Enter a valid email address.';
        emailInput.focus();
        return;
      }
      status.textContent = 'Sending your code…';
      emailSend.disabled = true;
      void sendEmailOtp(email)
        .then(() => {
          otpEmail = email;
          // Move to step 2: hide email input, reveal the code input.
          emailForm.hidden = true;
          codeForm.hidden = false;
          codeInput.value = '';
          codeInput.focus();
          status.textContent = `We emailed a 6-digit code to ${email}.`;
        })
        .catch((err) => {
          emailSend.disabled = false;
          status.textContent =
            err instanceof Error ? `Couldn't send the code: ${err.message}` : 'Send failed.';
        });
    };
    emailSend.addEventListener('click', sendCode);
    emailInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); sendCode(); }
    });

    // Step 2 — verify the code → session established IN PLACE.
    const verifyCode = (): void => {
      const code = codeInput.value.trim();
      if (code.length < 4) {
        status.textContent = 'Enter the code from your email.';
        codeInput.focus();
        return;
      }
      status.textContent = 'Verifying…';
      codeVerify.disabled = true;
      void verifyEmailOtp({ email: otpEmail, code })
        .then((session) => {
          establishSession(session);
          status.textContent = 'Signed in!';
          dismiss();
          resolve();
          onSignedIn?.();
        })
        .catch((err) => {
          codeVerify.disabled = false;
          status.textContent = authErrorMessage(err, `That didn't work: %m`);
          codeInput.focus();
        });
    };
    codeVerify.addEventListener('click', verifyCode);
    codeInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); verifyCode(); }
    });

    // Secondary fallback — magic link instead of a code (leaves the page).
    magicLinkBtn.addEventListener('click', () => {
      const email = (emailInput.value || otpEmail).trim();
      if (!emailOk(email)) {
        emailForm.hidden = false;
        status.textContent = 'Enter your email first, then tap the link option.';
        emailInput.focus();
        return;
      }
      status.textContent = 'Sending your sign-in link…';
      magicLinkBtn.disabled = true;
      if (cartridgeId) setPendingLaunch(cartridgeId);
      void sendMagicLink({ email, redirectUrl: `${window.location.origin}/auth/callback` })
        .then(() => { status.textContent = 'Check your email for a sign-in link.'; })
        .catch((err) => {
          magicLinkBtn.disabled = false;
          status.textContent =
            err instanceof Error ? `Couldn't send the link: ${err.message}` : 'Send failed.';
        });
    });
  });
}

/**
 * Paywall (grant:'paywall') — real pricing (sc-6943). Renders the three
 * passes; a Buy tap starts a PayPal checkout for that pass and redirects
 * the browser to PayPal's approve page. The pending cartridge is stashed
 * first so /billing/return can relaunch it after the purchase completes.
 *
 * Because a purchase LEAVES the page (redirect), the returned promise only
 * resolves on dismiss (Back to cabinet / backdrop) — the buy paths navigate
 * away and never resolve here (same contract as the login wall's Google path).
 */
export function showPaywall(
  resetsAtUtc?: string,
  cartridgeId?: string,
  reason: PaywallReason = 'exhausted',
): Promise<PaywallResult> {
  return new Promise((resolve) => {
    dismiss();
    const { root, card, closeBtn, status } = buildWall('paywall', reason);
    activeWall = root;

    // The refill countdown only makes sense when the player is actually OUT of
    // tokens — in the voluntary upgrade context it's noise (they have tokens).
    if (resetsAtUtc && reason === 'exhausted') {
      const reset = new Date(resetsAtUtc);
      const mins = Math.max(0, Math.round((reset.getTime() - Date.now()) / 60000));
      const h = Math.floor(mins / 60);
      status.textContent = h > 0
        ? `Free tokens refill in about ${h}h ${mins % 60}m.`
        : `Free tokens refill in about ${mins}m.`;
    }

    // Rewarded "Watch an ad for 1 token" — a free alternative to buying, shown
    // ONLY when a rewarded provider is actually ready (network live OR bundled
    // house video). Hidden entirely otherwise: no dead button. The manager
    // owns the paying/anon gates; here we just offer and react.
    let rewardedBtn: HTMLButtonElement | null = null;
    if (cartridgeId && getAdManager().isRewardedReady()) {
      const rewarded = document.createElement('button');
      rewardedBtn = rewarded;
      rewarded.type = 'button';
      rewarded.className = 'play-gate-wall__rewarded';
      rewarded.textContent = '▶  Watch an ad — earn 1 token';

      let watching = false;
      const idle = '▶  Watch an ad — earn 1 token';
      rewarded.addEventListener('click', () => {
        if (watching) return;
        watching = true;
        rewarded.disabled = true;
        rewarded.textContent = 'Loading ad…';
        void getAdManager().offerRewarded('paywall', cartridgeId).then((out) => {
          if (out.granted) {
            // Server credited a token. Close the wall and signal the caller to
            // relaunch the pending run. Don't trust the count — the wall closes
            // and the run's own start-spend re-reads the wallet.
            rewarded.textContent = '✓  Token added — starting…';
            window.setTimeout(() => { dismiss(); resolve({ tokenEarned: true }); }, 700);
            return;
          }
          // Not granted — copy per reason, re-enable where it makes sense.
          watching = false;
          rewarded.disabled = false;
          switch (out.reason) {
            case 'signin':
              rewarded.textContent = 'Sign in to earn tokens';
              rewarded.disabled = true; // no video played; sign-in is the path
              break;
            case 'cap':
              rewarded.textContent = 'Daily ad limit reached — come back tomorrow';
              rewarded.disabled = true;
              break;
            case 'dismissed':
              rewarded.textContent = idle; // they bailed; let them retry
              break;
            case 'entitled':
              rewarded.remove(); // pass holder shouldn't see this at all
              break;
            default:
              rewarded.textContent = 'No ad available — try again';
          }
        });
      });
    }

    // Build the pricing tiers and insert them ABOVE the status line.
    const tiers = document.createElement('div');
    tiers.className = 'play-gate-wall__tiers';
    let busy = false;

    const unlockTiers = (): void => {
      busy = false;
      for (const b of tiers.querySelectorAll('button')) b.disabled = false;
    };

    // Fallback: the original full-page redirect to PayPal. Used only when the
    // in-page SDK can't load (blocked/failed), so the buyer can always pay.
    const redirectFallback = (pass: PassOption): void => {
      const origin = window.location.origin;
      const returnUrl = `${origin}${BILLING_RETURN_PATH}?kind=${encodeURIComponent(pass.kind)}`;
      const cancelUrl = `${origin}${BILLING_CANCEL_PATH}`;
      if (cartridgeId) setPendingLaunch(cartridgeId);
      void startCheckout(pass.kind, returnUrl, cancelUrl)
        .then((res) => {
          if (!res.approveUrl) throw new Error('No approve URL returned');
          window.location.assign(res.approveUrl);
        })
        .catch((err) => {
          unlockTiers();
          status.textContent =
            err instanceof Error ? `Couldn't start checkout: ${err.message}` : 'Checkout failed.';
        });
    };

    const buy = (pass: PassOption, btn: HTMLButtonElement): void => {
      if (busy) return;
      busy = true;
      void btn;
      // Lock every buy button so a second tap can't start a 2nd checkout.
      for (const b of tiers.querySelectorAll('button')) b.disabled = true;
      status.textContent = `Starting checkout for the ${pass.label}…`;

      if (!isPayPalSdkConfigured()) { redirectFallback(pass); return; }

      // In-page PayPal modal — the site never unloads.
      void openCheckoutModal(pass).then((result) => {
        switch (result.status) {
          case 'entitled':
            // Paid & pass active. Close the wall and relaunch the pending run.
            dismiss();
            resolve({ tokenEarned: false, entitled: true });
            break;
          case 'unavailable':
            // SDK couldn't load — fall back to the redirect so they can still pay.
            status.textContent = 'Opening secure checkout…';
            redirectFallback(pass);
            break;
          case 'cancelled':
            unlockTiers();
            status.textContent = '';
            break;
          case 'error':
          default:
            unlockTiers();
            status.textContent = 'Checkout didn’t complete. Please try again.';
            break;
        }
      });
    };

    for (const pass of PASS_CATALOG) {
      tiers.append(buildTier(pass, buy));
    }
    // status is the last child appended by buildWall (before closeBtn moved);
    // insert tiers right before it, and the rewarded button (if any) above
    // tiers as the free-first option.
    card.insertBefore(tiers, status);
    if (rewardedBtn) card.insertBefore(rewardedBtn, tiers);

    document.body.appendChild(root);
    const close = (): void => { dismiss(); resolve({ tokenEarned: false }); };
    closeBtn.addEventListener('click', close);
    root.addEventListener('click', (e) => { if (e.target === root) close(); });
  });
}

/** One pricing tier: label, price + cadence, blurb, and a Buy button. */
function buildTier(
  pass: PassOption,
  onBuy: (pass: PassOption, btn: HTMLButtonElement) => void
): HTMLElement {
  const tier = document.createElement('div');
  tier.className = pass.featured
    ? 'play-gate-wall__tier play-gate-wall__tier--featured'
    : 'play-gate-wall__tier';

  if (pass.featured) {
    const badge = document.createElement('span');
    badge.className = 'play-gate-wall__tier-badge';
    badge.textContent = 'BEST VALUE';
    tier.append(badge);
  }

  const head = document.createElement('div');
  head.className = 'play-gate-wall__tier-head';

  // Left of the head row: the pass name + (when on offer) the LAUNCH OFFER pill.
  const nameGroup = document.createElement('span');
  nameGroup.className = 'play-gate-wall__tier-namegroup';
  const label = document.createElement('span');
  label.className = 'play-gate-wall__tier-label';
  label.textContent = pass.label;
  nameGroup.append(label);
  if (pass.listPriceUsd) {
    const offer = document.createElement('span');
    offer.className = 'play-gate-wall__tier-offer';
    offer.textContent = 'LAUNCH OFFER';
    nameGroup.append(offer);
  }

  // Price block: struck launch/list price (if any) beside the real charged price.
  const priceWrap = document.createElement('span');
  priceWrap.className = 'play-gate-wall__tier-pricewrap';
  if (pass.listPriceUsd) {
    const was = document.createElement('span');
    was.className = 'play-gate-wall__tier-was';
    was.textContent = `$${pass.listPriceUsd}`;
    was.setAttribute('aria-label', `Regular price $${pass.listPriceUsd}`);
    priceWrap.append(was);
  }
  const price = document.createElement('span');
  price.className = 'play-gate-wall__tier-price';
  price.textContent = `$${pass.priceUsd}`;
  priceWrap.append(price);
  head.append(nameGroup, priceWrap);

  const cadence = document.createElement('div');
  cadence.className = 'play-gate-wall__tier-cadence';
  cadence.textContent = pass.cadence;

  const blurb = document.createElement('p');
  blurb.className = 'play-gate-wall__tier-blurb';
  blurb.textContent = pass.blurb;

  const buyBtn = document.createElement('button');
  buyBtn.type = 'button';
  buyBtn.className = 'play-gate-wall__tier-buy';
  buyBtn.textContent = `Get ${pass.label}`;
  buyBtn.addEventListener('click', () => onBuy(pass, buyBtn));

  tier.append(head, cadence, blurb, buyBtn);
  return tier;
}

/**
 * Back-compat alias: the gate calls showPaywallStub in a few places. Now a
 * thin forward to the real paywall (sc-6943). Kept so callers that don't
 * have a cartridge id (fail-closed 'error' branch) still work.
 */
export function showPaywallStub(resetsAtUtc?: string): Promise<PaywallResult> {
  return showPaywall(resetsAtUtc);
}

// ---------------------------------------------------------------- DOM

interface WallRefs {
  root: HTMLElement;
  card: HTMLElement;
  googleBtn: HTMLButtonElement;
  emailBtn: HTMLButtonElement;
  emailForm: HTMLElement;
  emailInput: HTMLInputElement;
  emailSend: HTMLButtonElement;
  codeForm: HTMLElement;
  codeInput: HTMLInputElement;
  codeVerify: HTMLButtonElement;
  magicLinkBtn: HTMLButtonElement;
  status: HTMLElement;
  closeBtn: HTMLButtonElement;
}

/**
 * Why the paywall is showing — drives the copy:
 *   'exhausted' → the player ran out of tokens (the gate blocked a launch)
 *   'upgrade'   → the player opened it voluntarily (footer CREDITS click) with
 *                 tokens still remaining; "out of tokens" would be a lie here.
 */
export type PaywallReason = 'exhausted' | 'upgrade';

function buildWall(kind: 'login' | 'paywall', reason: PaywallReason = 'exhausted'): WallRefs {
  const root = document.createElement('div');
  root.className = 'play-gate-wall';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');

  const card = document.createElement('div');
  card.className = 'play-gate-wall__card';

  const upgrade = kind === 'paywall' && reason === 'upgrade';

  const eyebrow = document.createElement('div');
  eyebrow.className = 'play-gate-wall__eyebrow';
  eyebrow.textContent = kind === 'login'
    ? 'INSERT COIN'
    : upgrade ? 'GO UNLIMITED' : 'OUT OF TOKENS';

  const heading = document.createElement('h2');
  heading.className = 'play-gate-wall__title';
  heading.textContent = kind === 'login'
    ? 'Sign in to keep playing'
    : upgrade ? 'Play without limits' : "You're out of tokens";

  const pitch = document.createElement('p');
  pitch.className = 'play-gate-wall__pitch';
  pitch.textContent = kind === 'login'
    ? 'Free account = 5 plays every day. No card, no catch.'
    : upgrade
      ? 'Skip the daily limit — grab a pass for unlimited play, no more waiting.'
      : 'Come back tomorrow for 5 more — or grab a pass for unlimited play right now.';

  card.append(eyebrow, heading, pitch);

  // login-only auth controls
  const googleBtn = document.createElement('button');
  const emailBtn = document.createElement('button');
  const emailForm = document.createElement('div');
  const emailInput = document.createElement('input');
  const emailSend = document.createElement('button');
  const codeForm = document.createElement('div');
  const codeInput = document.createElement('input');
  const codeVerify = document.createElement('button');
  const magicLinkBtn = document.createElement('button');
  if (kind === 'login') {
    googleBtn.type = 'button';
    googleBtn.className = 'play-gate-wall__google';
    googleBtn.textContent = 'Continue with Google';

    emailBtn.type = 'button';
    emailBtn.className = 'play-gate-wall__email-toggle';
    emailBtn.textContent = 'Use email instead';

    // Step 1: email input → "Send code".
    emailForm.className = 'play-gate-wall__email-form';
    emailForm.hidden = true;
    emailInput.type = 'email';
    emailInput.placeholder = 'you@example.com';
    emailInput.className = 'play-gate-wall__email-input';
    emailInput.autocomplete = 'email';
    emailSend.type = 'button';
    emailSend.className = 'play-gate-wall__email-send';
    emailSend.textContent = 'Send code';
    emailForm.append(emailInput, emailSend);

    // Step 2: 6-digit code input → "Verify". Revealed after send.
    codeForm.className = 'play-gate-wall__email-form';
    codeForm.hidden = true;
    codeInput.type = 'text';
    codeInput.inputMode = 'numeric';
    codeInput.autocomplete = 'one-time-code';
    codeInput.maxLength = 8;
    codeInput.placeholder = '6-digit code';
    codeInput.className = 'play-gate-wall__email-input';
    codeVerify.type = 'button';
    codeVerify.className = 'play-gate-wall__email-send';
    codeVerify.textContent = 'Verify';
    codeForm.append(codeInput, codeVerify);

    // Secondary fallback: magic link instead of a code.
    magicLinkBtn.type = 'button';
    magicLinkBtn.className = 'play-gate-wall__magiclink';
    magicLinkBtn.textContent = 'Email me a link instead';

    card.append(googleBtn, emailBtn, emailForm, codeForm, magicLinkBtn);
  }

  const status = document.createElement('p');
  status.className = 'play-gate-wall__status';
  status.setAttribute('aria-live', 'polite');
  card.append(status);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'play-gate-wall__close';
  closeBtn.textContent = kind === 'login' ? 'Maybe later' : 'Back to cabinet';
  card.append(closeBtn);

  root.append(card);
  return {
    root, card, googleBtn, emailBtn, emailForm, emailInput, emailSend,
    codeForm, codeInput, codeVerify, magicLinkBtn, status, closeBtn,
  };
}
